import { Transition } from "@headlessui/react"
import { Form, Head } from "@inertiajs/react"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"

import { ResendVerificationButton } from "@/components/email-verification/resend-verification-button"
import HeadingSmall from "@/components/heading-small"
import InputError from "@/components/input-error"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { useCurrentContext } from "@/hooks/use-current-context"
import AppLayout from "@/layouts/app-layout"
import SettingsLayout from "@/layouts/settings/layout"
import { settingsEmailPath } from "@/routes"
import type { BreadcrumbItem } from "@/types"

export default function Email() {
  const { t } = useTranslation()
  const { currentUser } = useCurrentContext()

  const breadcrumbs: BreadcrumbItem[] = useMemo(
    () => [
      {
        title: t("settings.emails.breadcrumb"),
        href: settingsEmailPath(),
      },
    ],
    [t],
  )

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={breadcrumbs[breadcrumbs.length - 1].title} />

      <SettingsLayout>
        <div className="space-y-6">
          <HeadingSmall
            title={t("settings.emails.title")}
            description={t("settings.emails.description")}
          />

          <Form
            method="patch"
            action={settingsEmailPath()}
            options={{
              preserveScroll: true,
            }}
            resetOnError={["password_challenge"]}
            resetOnSuccess={["password_challenge"]}
            className="space-y-6"
          >
            {({ errors, processing, recentlySuccessful }) => (
              <>
                <div className="grid gap-2">
                  <Label htmlFor="email">
                    {t("settings.emails.email_label")}
                    <span
                      className="text-destructive ml-0.5"
                      aria-hidden="true"
                    >
                      *
                    </span>
                  </Label>

                  <Input
                    id="email"
                    type="email"
                    name="email"
                    className="mt-1 block w-full"
                    defaultValue={currentUser?.email ?? ""}
                    aria-required="true"
                    aria-invalid={Boolean(errors.email)}
                    autoComplete="username"
                    placeholder={t("settings.emails.email_placeholder")}
                  />

                  <InputError className="mt-2" messages={errors.email} />
                </div>

                {/* OKS-799 — inverse-gated inline fallback.
                    NON-CREATORS ONLY: `role !== "creator"`.

                    The relocated `<UnverifiedEmailBanner>` is creator-scoped
                    by design (it describes a creator-account concern), so for
                    a creator it renders above this form and carries the resend
                    — and this block must NOT render, or the page would host
                    two controls with INDEPENDENT component-local cooldown
                    timers (only the server cooldown is durable), leaving one
                    rendering idle while the server already refuses.

                    But non-creators CAN be unverified, and by a routine
                    action: `user.rb:437-439` runs
                    `before_validation if: :email_changed?, on: :update` →
                    `self.verified = false`, ROLE-AGNOSTICALLY. Any brand /
                    agency / admin who changes their email here lands
                    unverified. The change fires one verification mail
                    (`emails_controller#resend_email_verification`) — but if
                    that mail is missed there is no second chance:
                    `password_resets_controller:23` scopes to
                    `verified: true`, so password reset refuses them too.
                    Deleting this block outright (as the first pass did) left
                    that cohort with zero in-app recovery.

                    So the invariant is preserved per-role rather than by
                    deletion: creators get the banner's CTA, non-creators get
                    this line — EXACTLY ONE control on this screen either way,
                    never two, never zero. The two gates are mutually
                    exclusive by construction (`role === "creator"` vs
                    `role !== "creator"`); if you ever loosen one, loosen the
                    other in the same edit or the count invariant breaks. */}
                {currentUser &&
                  currentUser.role !== "creator" &&
                  !currentUser.verified && (
                    <div className="text-muted-foreground -mt-4 flex flex-wrap items-center gap-3 text-sm">
                      {/* `<ResendVerificationButton>` renders a plain
                          `<button type="button">` firing `router.post(...)`,
                          NOT a wrapping `<Form>` — so nesting inside the
                          email-change Form is safe (no inner `<form>`, no
                          nested-form parser quirk). Keeps its DEFAULT testid;
                          the banner's CTA overrides to
                          `unverified-email-banner-action`, so the two never
                          collide in a query.

                          OKS-874 — `size="sm"` dropped so this falls through
                          to `default` and takes the 44px mobile floor. `sm` is
                          NOT floored by OKS-874: removing the prop is what
                          reaches 44px here, not the primitive change. */}
                      <span>{t("settings.emails.unverified_prefix")}</span>
                      <ResendVerificationButton variant="outline" />
                    </div>
                  )}

                <div className="grid gap-2">
                  <Label htmlFor="password_challenge">
                    {t("settings.emails.password_challenge_label")}
                  </Label>

                  <Input
                    id="password_challenge"
                    name="password_challenge"
                    type="password"
                    className="mt-1 block w-full"
                    autoComplete="current-password"
                    placeholder={t(
                      "settings.emails.password_challenge_placeholder",
                    )}
                  />

                  <InputError messages={errors.password_challenge} />
                </div>

                <div className="flex items-center gap-4">
                  <Button disabled={processing}>
                    {t("settings.emails.save")}
                  </Button>

                  <Transition
                    show={recentlySuccessful}
                    enter="transition ease-in-out"
                    enterFrom="opacity-0"
                    leave="transition ease-in-out"
                    leaveTo="opacity-0"
                  >
                    <p className="text-sm text-neutral-600">
                      {t("settings.emails.saved")}
                    </p>
                  </Transition>
                </div>
              </>
            )}
          </Form>
        </div>
      </SettingsLayout>
    </AppLayout>
  )
}
